import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider, useLang } from './i18n/LanguageContext';
import { Toaster, ToastBar, toast as hotToast } from 'react-hot-toast';
import toast from 'react-hot-toast';

// Layout
import Layout from './components/layout/Layout';
import AppShell from './components/os/AppShell';

const NEW_UI = process.env.REACT_APP_NEW_UI_ENABLED === 'true';

// Eager-load Login (first screen)
import Login from './pages/Login';

// ── OS pages (gated by REACT_APP_NEW_UI_ENABLED) ──────────────────────────────
const DashboardOS        = lazy(() => import('./pages/DashboardOS'));
const SalonOverviewOS    = lazy(() => import('./pages/SalonOverviewOS'));
const SocialOS           = lazy(() => import('./pages/SocialOS'));
const BillingOS          = lazy(() => import('./pages/BillingOS'));
const StaffNotesOS       = lazy(() => import('./pages/StaffNotesOS'));
const StaffTimeOS        = lazy(() => import('./pages/StaffTimeOS'));
const AppointmentsOS     = lazy(() => import('./pages/AppointmentsOS'));
const TransactionsOS     = lazy(() => import('./pages/TransactionsOS'));
const TargetsOS          = lazy(() => import('./pages/TargetsOS'));
const ServiceProfitOS    = lazy(() => import('./pages/ServiceProfitabilityOS'));
const PricingOS          = lazy(() => import('./pages/PricingOS'));
const ServiceDetailOS    = lazy(() => import('./pages/ServiceDetailOS'));
const SalonCalendarOS    = lazy(() => import('./pages/SalonCalendarOS'));
const CustomerRiskOS     = lazy(() => import('./pages/CustomerRiskOS'));
const CustomersOS        = lazy(() => import('./pages/CustomersOS'));
const CustomerDetailOS   = lazy(() => import('./pages/CustomerDetailOS'));
const VipCardsOS         = lazy(() => import('./pages/VipCardsOS'));
const ReviewsOS          = lazy(() => import('./pages/ReviewsOS'));
const WalkInsOS          = lazy(() => import('./pages/WalkInsOS'));
const BridalJobsOS       = lazy(() => import('./pages/BridalJobsOS'));
const BridalJobOS        = lazy(() => import('./pages/BridalJobOS'));
const ServicesOS         = lazy(() => import('./pages/ServicesOS'));
const ConcernsOS         = lazy(() => import('./pages/ConcernsOS'));
const ConcernDetailOS    = lazy(() => import('./pages/ConcernDetailOS'));
const InventoryOS        = lazy(() => import('./pages/InventoryOS'));
const PackagesOS         = lazy(() => import('./pages/PackagesOS'));
const MembershipsOS      = lazy(() => import('./pages/MembershipsOS'));
const CombosOS           = lazy(() => import('./pages/CombosOS'));
const OffersOS           = lazy(() => import('./pages/OffersOS'));
const EmployeesOS        = lazy(() => import('./pages/EmployeesOS'));
const EmployeeDetailOS   = lazy(() => import('./pages/EmployeeDetailOS'));
const PayrollOS          = lazy(() => import('./pages/PayrollOS'));
const StaffRosterOS      = lazy(() => import('./pages/StaffRosterOS'));
const StaffPerformanceOS = lazy(() => import('./pages/StaffPerformanceOS'));
const RecognitionOS = lazy(() => import('./pages/RecognitionOS'));
const ReportsOS          = lazy(() => import('./pages/ReportsOS'));
const CustomerInsightsOS = lazy(() => import('./pages/CustomerInsightsOS'));
const ServiceInsightsOS  = lazy(() => import('./pages/ServiceInsightsOS'));
const ServiceTrendsOS    = lazy(() => import('./pages/ServiceTrendsOS'));
const StaffInsightsOS    = lazy(() => import('./pages/StaffInsightsOS'));
const ProductInsightsOS  = lazy(() => import('./pages/ProductInsightsOS'));
const MarketingOS        = lazy(() => import('./pages/MarketingOS'));
const ReferralsOS        = lazy(() => import('./pages/ReferralsOS'));
const StylistDirectoryOS = lazy(() => import('./pages/StylistDirectoryOS'));
const UserManagementOS   = lazy(() => import('./pages/UserManagementOS'));
const LedgerOS           = lazy(() => import('./pages/LedgerOS'));
const TasksOS            = lazy(() => import('./pages/TasksOS'));
const AccountsOS         = lazy(() => import('./pages/AccountsOS'));
const ProductUsageOS     = lazy(() => import('./pages/ProductUsageOS'));
const SOPOS              = lazy(() => import('./pages/SOPOS'));
const WhatsAppOS         = lazy(() => import('./pages/WhatsAppOS'));
const SettingsOS         = lazy(() => import('./pages/SettingsOS'));
const SystemConfigOS     = lazy(() => import('./pages/SystemConfigOS'));
const DocumentsOS        = lazy(() => import('./pages/DocumentsOS'));
const AiConsultationsOS  = lazy(() => import('./pages/AiConsultationsOS'));
const AiConsultationDetailOS = lazy(() => import('./pages/AiConsultationDetailOS'));
const BeautyProfileOS    = lazy(() => import('./pages/BeautyProfileOS'));
const ServiceRemindersOS = lazy(() => import('./pages/ServiceRemindersOS'));
const DesignSystem       = lazy(() => import('./pages/DesignSystem'));
const AttendanceTrackerOS    = lazy(() => import('./pages/AttendanceTrackerOS'));

// ── Legacy pages ──────────────────────────────────────────────────────────────
const Appointments    = lazy(() => import('./pages/Appointments'));
const Billing         = lazy(() => import('./pages/Billing'));
const Transactions    = lazy(() => import('./pages/Transactions'));
const Customers       = lazy(() => import('./pages/Customers'));
const Employees       = lazy(() => import('./pages/Employees'));
const EmployeeDetail  = lazy(() => import('./pages/EmployeeDetail'));
const Inventory       = lazy(() => import('./pages/Inventory'));
const Services        = lazy(() => import('./pages/Services'));
const Packages        = lazy(() => import('./pages/Packages'));
const Marketing       = lazy(() => import('./pages/Marketing'));
const Reports         = lazy(() => import('./pages/Reports'));
const CustomerInsights = lazy(() => import('./pages/CustomerInsights'));
const ServiceInsights  = lazy(() => import('./pages/ServiceInsights'));
const StaffInsights    = lazy(() => import('./pages/StaffInsights'));
const ProductInsights  = lazy(() => import('./pages/ProductInsights'));
const Settings        = lazy(() => import('./pages/Settings'));
const UserManagement  = lazy(() => import('./pages/UserManagement'));
const Offers          = lazy(() => import('./pages/Offers'));
const Payroll         = lazy(() => import('./pages/Payroll'));
const PublicBooking   = lazy(() => import('./pages/public/Book'));
const CheckIn         = lazy(() => import('./pages/public/CheckIn'));
const PrivacyPolicy   = lazy(() => import('./pages/PrivacyPolicy'));
const DigitalCard     = lazy(() => import('./pages/DigitalCard'));
const Hairstyles      = lazy(() => import('./pages/Hairstyles'));
const Accounts        = lazy(() => import('./pages/Accounts'));
const ProductUsage    = lazy(() => import('./pages/ProductUsage'));
const StaffPerformance = lazy(() => import('./pages/StaffPerformance'));
const StaffRoster     = lazy(() => import('./pages/StaffRoster'));
const Referrals       = lazy(() => import('./pages/Referrals'));
const StylistDirectory = lazy(() => import('./pages/StylistDirectory'));
const Ledger          = lazy(() => import('./pages/Ledger'));
const SOP             = lazy(() => import('./pages/SOP'));

// Utilities
import { hasAccess } from './utils/permissions';
import apiClient from './utils/api';

// Page loading fallback
const PageLoader = () => (
  <div className="flex items-center justify-center h-64">
    <div className="text-center">
      <div className="w-10 h-10 border-4 border-gold-200 border-t-gold-500 rounded-full animate-spin mx-auto mb-3" />
      <p className="text-stone-400 text-sm">Loading...</p>
    </div>
  </div>
);

// Protected Route Component
/**
 * The old /bridal/jobs/:id address, carried across to /events/:id.
 *
 * A bare <Navigate> cannot do this — the id has to be read out of the old path
 * and put back into the new one, or every bookmarked job lands on the list.
 */
function RedirectJob() {
  const { id } = useParams();
  return <Navigate to={`/events/${id}`} replace />;
}

const ProtectedRoute = ({ element, requiredPage }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-stone-900 to-stone-800">
        <div className="text-center">
          <div className="font-display text-4xl text-white mb-4 animate-pulse">
            Gumma's Glam House
          </div>
          <div className="text-gold-300 text-lg">Loading...</div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requiredPage && !hasAccess(user?.role, requiredPage)) {
    const fallback = hasAccess(user?.role, 'billing') ? '/' : '/appointments';
    return <Navigate to={fallback} replace />;
  }

  const Shell = NEW_UI ? AppShell : Layout;
  return (
    <Shell>
      <Suspense fallback={<PageLoader />}>
        {element}
      </Suspense>
    </Shell>
  );
};

/*
 * The language switch on the customer screens.
 *
 * Rendered here rather than inside the two pages because each of them is three
 * or four full-screen panes — the phone screen, the menu, the cart sheet, the
 * confirmation — and the switch has to be on all of them. One fixed chip at the
 * app level is on every pane by construction, including panes added later.
 *
 * It is opaque on purpose: it sits over a dark photograph on the first screen
 * and over cream on the rest, and has to read on both.
 */
const PublicLanguageToggle = () => {
  const { lang, setLang, isPublic } = useLang();
  if (!isPublic) return null;
  const options = [['te', 'తెలుగు'], ['en', 'English']];
  return (
    <div className="fixed top-4 right-4 z-[60] flex rounded-full overflow-hidden"
      style={{ background: 'rgba(255,255,255,0.92)', border: '1px solid #D4A853',
        boxShadow: '0 2px 10px rgba(0,0,0,0.18)' }}>
      {options.map(([code, label]) => (
        <button key={code} onClick={() => setLang(code)}
          aria-pressed={lang === code}
          data-no-i18n
          className="px-3 py-1.5 text-[13px] font-semibold"
          style={{
            background: lang === code ? '#D4A853' : 'transparent',
            color: lang === code ? '#1C1917' : '#6E6454',
          }}>
          {label}
        </button>
      ))}
    </div>
  );
};

// Global floating WhatsApp button for public pages
const WhatsAppFloat = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const publicPaths = ['/login', '/book', '/checkin', '/privacy-policy', '/card'];
  const isPublic = publicPaths.some((p) => location.pathname.startsWith(p));
  if (isAuthenticated && !isPublic) return null;
  if (location.pathname.startsWith('/book/')) return null;
  // The check-in screen has its own bottom bar; a floating bubble lands on top of it.
  if (location.pathname.startsWith('/checkin/')) return null;
  return (
    <a href="https://wa.me/917277055556" target="_blank" rel="noopener noreferrer"
      className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-transform hover:scale-110"
      style={{ backgroundColor: '#25D366' }}
      aria-label="Chat on WhatsApp">
      <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
      </svg>
    </a>
  );
};

/**
 * Watches today's book every 30s and raises anything that arrived without a
 * person at the desk typing it: an online booking, a WhatsApp booking, and a
 * walk-in who checked themselves in on the QR menu.
 *
 * The toast does not expire. Reception is not looking at the screen when
 * somebody walks in — that is the whole point of the notification — so one
 * that fades after twelve seconds is one that is missed. It stays until it is
 * closed, which also leaves a stack of them as a record of what came in while
 * nobody was watching.
 */
const ARRIVALS = {
  'walk-in': { label: '🚶 Checked in at the desk', to: '/walk-ins', tail: 'Tap to open check-ins' },
  whatsapp: { label: '📅 New booking — 📱 WhatsApp', to: '/appointments', tail: 'Tap to open appointments' },
  online: { label: '📅 New booking — 🌐 Online', to: '/appointments', tail: 'Tap to open appointments' },
};

function BookingNotifier() {
  const { isAuthenticated } = useAuth();
  const seenIds = React.useRef(new Set());
  const initialized = React.useRef(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') Notification.requestPermission();

    const pad = (n) => String(n).padStart(2, '0');
    const toDateStr = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    const showNotification = (b) => {
      const kind = ARRIVALS[b.source] || ARRIVALS.online;
      const services = Array.isArray(b.services)
        ? b.services.map((s) => s.serviceName || s.name).filter(Boolean).join(', ')
        : '';
      toast.custom(
        (t) => (
          <div
            onClick={() => { toast.dismiss(t.id); window.location.href = kind.to; }}
            style={{
              background: t.visible ? '#1c1917' : 'transparent',
              border: '0.0833rem solid #a37c1a',
              borderRadius: '1rem',
              padding: '1.1667rem 1.5rem',
              cursor: 'pointer',
              minWidth: '23.3333rem',
              boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
              transition: 'all 0.3s ease',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'start', gap: '0.5rem', marginBottom: '0.3333rem' }}>
              <div style={{ color: '#d4a843', fontWeight: 700, fontSize: '1.0833rem', flex: 1 }}>
                {kind.label}
              </div>
              {/* The only way it goes away. stopPropagation so closing it does
                  not also navigate. */}
              <button
                onClick={(e) => { e.stopPropagation(); toast.dismiss(t.id); }}
                aria-label="Close"
                style={{
                  color: '#a8a29e', fontSize: '1.25rem', lineHeight: 1,
                  padding: '0 0.25rem', background: 'none', border: 'none', cursor: 'pointer',
                }}>
                ×
              </button>
            </div>
            <div style={{ color: '#fff', fontWeight: 600, fontSize: '1.25rem' }}>{b.customerName}</div>
            <div style={{ color: '#a8a29e', fontSize: '1rem', marginTop: 2 }}>{services}</div>
            <div style={{ color: '#78716c', fontSize: '0.9167rem', marginTop: '0.3333rem' }}>
              {b.startTime} · {kind.tail}
            </div>
          </div>
        ),
        { duration: Infinity, position: 'top-right' }
      );
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        new Notification(`${b.customerName} — ${kind.label.replace(/^\S+\s/, '')}`, {
          body: `${services}${b.startTime ? ` at ${b.startTime}` : ''}`,
          icon: '/favicon.ico',
          // Stays on screen until it is dismissed, for the same reason the
          // toast does.
          requireInteraction: true,
        });
      }
    };

    const poll = async () => {
      try {
        const today = toDateStr(new Date());
        const res = await apiClient.get('/appointments', { params: { date: today, limit: 100 } });
        const appts = Array.isArray(res?.data) ? res.data : [];

        if (!initialized.current) {
          appts.forEach((a) => seenIds.current.add(a.id));
          initialized.current = true;
          return;
        }

        appts
          .filter((a) => !seenIds.current.has(a.id) && ARRIVALS[a.source])
          .forEach((a) => {
            seenIds.current.add(a.id);
            showNotification(a);
          });

        appts.forEach((a) => seenIds.current.add(a.id));
      } catch (_) {}
    };

    poll();
    const interval = setInterval(poll, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);
  return null;
}

// Helper: OS route placed before legacy route so React Router v6 matches first
const osRoute = (path, osEl, legacyEl, page) => (
  <>
    {NEW_UI && <Route path={path} element={<ProtectedRoute element={osEl} requiredPage={page} />} />}
    <Route path={path} element={<ProtectedRoute element={legacyEl} requiredPage={page} />} />
  </>
);

function App() {
  return (
    <>
      <Router>
        <LanguageProvider>
        <AuthProvider>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/book/:salonId" element={<Suspense fallback={<PageLoader />}><PublicBooking /></Suspense>} />
            {/* The QR on the desk points here. Public, no auth — it is a
                stranger's phone. */}
            <Route path="/checkin/:salonId" element={<Suspense fallback={<PageLoader />}><CheckIn /></Suspense>} />
            <Route path="/privacy-policy" element={<Suspense fallback={<PageLoader />}><PrivacyPolicy /></Suspense>} />
            <Route path="/card" element={<Suspense fallback={<PageLoader />}><DigitalCard /></Suspense>} />
            <Route path="/hairstyles" element={<Suspense fallback={<PageLoader />}><Hairstyles /></Suspense>} />

            {/* Design system (dev only) */}
            {process.env.NODE_ENV !== 'production' && (
              <Route path="/design-system" element={<ProtectedRoute element={<DesignSystem />} />} />
            )}

            {/* Bridal */}
            {/* No legacy twin: this page did not exist before the OS UI. */}
            <Route path="/staff-notes" element={<ProtectedRoute element={<StaffNotesOS />} requiredPage="staff-notes" />} />
            <Route path="/staff-time" element={<ProtectedRoute element={<StaffTimeOS />} requiredPage="staff-time" />} />
            {NEW_UI && <Route path="/events" element={<ProtectedRoute element={<BridalJobsOS />} requiredPage="bridal" />} />}
            {NEW_UI && <Route path="/events/:id" element={<ProtectedRoute element={<BridalJobOS />} requiredPage="bridal" />} />}
            {/* The old addresses still work. Someone has these bookmarked, and a
                dead link is a worse outcome than a tidy URL scheme. */}
            {NEW_UI && <Route path="/bridal/jobs" element={<Navigate to="/events" replace />} />}
            {NEW_UI && <Route path="/bridal/jobs/:id" element={<RedirectJob />} />}

            {NEW_UI && <Route path="/attendance-tracker" element={<ProtectedRoute element={<AttendanceTrackerOS />} requiredPage="attendance-tracker" />} />}

            {/* Dashboard */}
            {NEW_UI && <Route path="/dashboard" element={<ProtectedRoute element={<DashboardOS />} />} />}
            {NEW_UI && <Route path="/overview" element={<ProtectedRoute element={<SalonOverviewOS />} requiredPage="reports" />} />}
            {NEW_UI && <Route path="/social" element={<ProtectedRoute element={<SocialOS />} requiredPage="social" />} />}
            {/* Everyone lands on the dashboard. It costs the desk one tap to
                reach billing and gives them the day's queue first, which is
                the trade the owner asked for. */}
            {/* Everyone lands on the dashboard. It costs the desk one tap to
                reach billing and gives them the day's queue first, which is
                the trade the owner asked for.

                No legacy fallback here: the pre-OS Dashboard is not imported,
                and naming it would have thrown the moment NEW_UI was off — the
                old UI keeps landing on billing, as it always did. */}
            <Route path="/" element={NEW_UI
              ? <ProtectedRoute element={<DashboardOS />} />
              : <ProtectedRoute element={<Billing />} requiredPage="billing" />} />

            {/* Operations */}
            {NEW_UI && <Route path="/billing" element={<ProtectedRoute element={<BillingOS />} requiredPage="billing" />} />}
            <Route path="/billing" element={<ProtectedRoute element={<Billing />} requiredPage="billing" />} />

            {NEW_UI && <Route path="/appointments" element={<ProtectedRoute element={<AppointmentsOS />} requiredPage="appointments" />} />}
            <Route path="/appointments" element={<ProtectedRoute element={<Appointments />} requiredPage="appointments" />} />

            <Route path="/targets" element={<ProtectedRoute element={<TargetsOS />} requiredPage="targets" />} />
            <Route path="/service-profitability" element={<ProtectedRoute element={<ServiceProfitOS />} requiredPage="service-profitability" />} />
            <Route path="/pricing" element={<ProtectedRoute element={<PricingOS />} requiredPage="pricing" />} />
            <Route path="/salon-calendar" element={<ProtectedRoute element={<SalonCalendarOS />} requiredPage="salon-calendar" />} />
            <Route path="/customer-risk" element={<ProtectedRoute element={<CustomerRiskOS />} requiredPage="customer-risk" />} />

            {NEW_UI && <Route path="/transactions" element={<ProtectedRoute element={<TransactionsOS />} requiredPage="billing" />} />}
            <Route path="/transactions" element={<ProtectedRoute element={<Transactions />} requiredPage="billing" />} />

            {NEW_UI && <Route path="/services" element={<ProtectedRoute element={<ServicesOS />} requiredPage="services" />} />}
            <Route path="/services" element={<ProtectedRoute element={<Services />} requiredPage="services" />} />
            <Route path="/services/:id" element={<ProtectedRoute element={<ServiceDetailOS />} requiredPage="services" />} />
            {/* The concern map: services and products side by side, and the
                holes between them. */}
            <Route path="/concerns" element={<ProtectedRoute element={<ConcernsOS />} requiredPage="services" />} />
            <Route path="/concerns/:id" element={<ProtectedRoute element={<ConcernDetailOS />} requiredPage="services" />} />

            <Route path="/whatsapp" element={<ProtectedRoute element={<WhatsAppOS />} requiredPage="whatsapp" />} />
            {NEW_UI && <Route path="/sop" element={<ProtectedRoute element={<SOPOS />} requiredPage="services" />} />}
            <Route path="/sop" element={<ProtectedRoute element={<SOP />} requiredPage="services" />} />

            {/* Gated on the customers page like the other Special Services: these are
                customer records, and anyone who may see a customer may see their looks. */}
            {NEW_UI && <Route path="/ai-consultations" element={<ProtectedRoute element={<AiConsultationsOS />} requiredPage="customers" />} />}
            {NEW_UI && <Route path="/ai-consultations/:id" element={<ProtectedRoute element={<AiConsultationDetailOS />} requiredPage="customers" />} />}
            {NEW_UI && <Route path="/beauty-profile" element={<ProtectedRoute element={<BeautyProfileOS />} requiredPage="beauty-profile" />} />}
            {NEW_UI && <Route path="/beauty-profile/:customerId" element={<ProtectedRoute element={<BeautyProfileOS />} requiredPage="beauty-profile" />} />}
            {NEW_UI && <Route path="/service-reminders" element={<ProtectedRoute element={<ServiceRemindersOS />} requiredPage="service-reminders" />} />}

            {NEW_UI && <Route path="/my-performance" element={<ProtectedRoute element={<StaffPerformanceOS />} requiredPage="my-performance" />} />}
            <Route path="/my-performance" element={<ProtectedRoute element={<StaffPerformance />} requiredPage="my-performance" />} />

            {/* Management */}
            {NEW_UI && <Route path="/customers" element={<ProtectedRoute element={<CustomersOS />} requiredPage="customers" />} />}
            {/* Before the list route would otherwise swallow it */}
            {NEW_UI && <Route path="/customers/:id" element={<ProtectedRoute element={<CustomerDetailOS />} requiredPage="customers" />} />}
            {NEW_UI && <Route path="/vip-cards" element={<ProtectedRoute element={<VipCardsOS />} requiredPage="vip-cards" />} />}
            {NEW_UI && <Route path="/reviews" element={<ProtectedRoute element={<ReviewsOS />} requiredPage="customers" />} />}
            {NEW_UI && <Route path="/walk-ins" element={<ProtectedRoute element={<WalkInsOS />} requiredPage="walk-ins" />} />}
            {/* The screen is called Check-ins now. The old path still answers,
                because it is in bookmarks, in the arrival notification and in
                every permission list; renaming it would be a migration, not a
                rename. */}
            {NEW_UI && <Route path="/check-ins" element={<ProtectedRoute element={<WalkInsOS />} requiredPage="walk-ins" />} />}
            <Route path="/customers" element={<ProtectedRoute element={<Customers />} requiredPage="customers" />} />

            {NEW_UI && <Route path="/employees" element={<ProtectedRoute element={<EmployeesOS />} requiredPage="employees" />} />}
            <Route path="/employees" element={<ProtectedRoute element={<Employees />} requiredPage="employees" />} />

            {NEW_UI && <Route path="/employees/:id" element={<ProtectedRoute element={<EmployeeDetailOS />} requiredPage="employees" />} />}
            <Route path="/employees/:id" element={<ProtectedRoute element={<EmployeeDetail />} requiredPage="employees" />} />

            {NEW_UI && <Route path="/staff-roster" element={<ProtectedRoute element={<StaffRosterOS />} requiredPage="staff-roster" />} />}
            <Route path="/staff-roster" element={<ProtectedRoute element={<StaffRoster />} requiredPage="staff-roster" />} />

            {NEW_UI && <Route path="/payroll" element={<ProtectedRoute element={<PayrollOS />} requiredPage="payroll" />} />}
            <Route path="/payroll" element={<ProtectedRoute element={<Payroll />} requiredPage="payroll" />} />

            {NEW_UI && <Route path="/inventory" element={<ProtectedRoute element={<InventoryOS />} requiredPage="inventory" />} />}
            <Route path="/inventory" element={<ProtectedRoute element={<Inventory />} requiredPage="inventory" />} />

            {NEW_UI && <Route path="/product-usage" element={<ProtectedRoute element={<ProductUsageOS />} requiredPage="inventory" />} />}
            <Route path="/product-usage" element={<ProtectedRoute element={<ProductUsage />} requiredPage="inventory" />} />

            {NEW_UI && <Route path="/accounts" element={<ProtectedRoute element={<AccountsOS />} requiredPage="accounts" />} />}
            <Route path="/accounts" element={<ProtectedRoute element={<Accounts />} requiredPage="accounts" />} />

            <Route path="/tasks" element={<ProtectedRoute element={<TasksOS />} requiredPage="tasks" />} />
            {NEW_UI && <Route path="/ledger" element={<ProtectedRoute element={<LedgerOS />} requiredPage="ledger" />} />}
            <Route path="/ledger" element={<ProtectedRoute element={<Ledger />} requiredPage="ledger" />} />

            {NEW_UI && <Route path="/stylist-directory" element={<ProtectedRoute element={<StylistDirectoryOS />} requiredPage="stylist-directory" />} />}
            <Route path="/stylist-directory" element={<ProtectedRoute element={<StylistDirectory />} requiredPage="stylist-directory" />} />

            {/* Growth */}
            {NEW_UI && <Route path="/referrals" element={<ProtectedRoute element={<ReferralsOS />} requiredPage="referrals" />} />}
            <Route path="/referrals" element={<ProtectedRoute element={<Referrals />} requiredPage="referrals" />} />

            {/* Memberships, packages and combos are three screens over one
                catalogue. They share the "packages" permission, because who may
                see and edit a plan does not change with which kind it is. */}
            {NEW_UI && <Route path="/memberships" element={<ProtectedRoute element={<MembershipsOS />} requiredPage="packages" />} />}
            {NEW_UI && <Route path="/combos" element={<ProtectedRoute element={<CombosOS />} requiredPage="packages" />} />}
            {NEW_UI && <Route path="/packages" element={<ProtectedRoute element={<PackagesOS />} requiredPage="packages" />} />}
            <Route path="/packages" element={<ProtectedRoute element={<Packages />} requiredPage="packages" />} />

            {NEW_UI && <Route path="/offers" element={<ProtectedRoute element={<OffersOS />} requiredPage="offers" />} />}
            <Route path="/offers" element={<ProtectedRoute element={<Offers />} requiredPage="offers" />} />

            {NEW_UI && <Route path="/marketing" element={<ProtectedRoute element={<MarketingOS />} requiredPage="marketing" />} />}
            <Route path="/marketing" element={<ProtectedRoute element={<Marketing />} requiredPage="marketing" />} />

            {/* Analytics */}
            {NEW_UI && <Route path="/reports" element={<ProtectedRoute element={<ReportsOS />} requiredPage="reports" />} />}
            <Route path="/reports" element={<ProtectedRoute element={<Reports />} requiredPage="reports" />} />

            {NEW_UI && <Route path="/customer-insights" element={<ProtectedRoute element={<CustomerInsightsOS />} requiredPage="reports" />} />}
            <Route path="/customer-insights" element={<ProtectedRoute element={<CustomerInsights />} requiredPage="reports" />} />

            <Route path="/service-trends" element={<ProtectedRoute element={<ServiceTrendsOS />} requiredPage="reports" />} />

            {NEW_UI && <Route path="/service-insights" element={<ProtectedRoute element={<ServiceInsightsOS />} requiredPage="reports" />} />}
            <Route path="/service-insights" element={<ProtectedRoute element={<ServiceInsights />} requiredPage="reports" />} />

            {NEW_UI && <Route path="/staff-insights" element={<ProtectedRoute element={<StaffInsightsOS />} requiredPage="reports" />} />}
            <Route path="/recognition" element={<ProtectedRoute element={<RecognitionOS />} requiredPage="recognition" />} />
            <Route path="/staff-insights" element={<ProtectedRoute element={<StaffInsights />} requiredPage="reports" />} />

            {NEW_UI && <Route path="/product-insights" element={<ProtectedRoute element={<ProductInsightsOS />} requiredPage="reports" />} />}
            <Route path="/product-insights" element={<ProtectedRoute element={<ProductInsights />} requiredPage="reports" />} />

            {/* System */}
            {NEW_UI && <Route path="/users" element={<ProtectedRoute element={<UserManagementOS />} requiredPage="users" />} />}
            <Route path="/users" element={<ProtectedRoute element={<UserManagement />} requiredPage="users" />} />

            {NEW_UI && <Route path="/settings" element={<ProtectedRoute element={<SettingsOS />} requiredPage="settings" />} />}
            <Route path="/system-config" element={<ProtectedRoute element={<SystemConfigOS />} requiredPage="system-config" />} />
            <Route path="/documents" element={<ProtectedRoute element={<DocumentsOS />} requiredPage="documents" />} />
            <Route path="/settings" element={<ProtectedRoute element={<Settings />} requiredPage="settings" />} />



            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <WhatsAppFloat />
          <PublicLanguageToggle />
          <BookingNotifier />
        </AuthProvider>
        </LanguageProvider>
      </Router>
      {/* Toaster OUTSIDE Router so it renders after Radix portals in DOM order,
          ensuring toasts always appear above modal overlays */}
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: '#FFFFFF',
            color: '#1C1917',
            border: '0.0833rem solid #E7E5E4',
            borderRadius: '1rem',
            boxShadow: '0 4px 16px rgba(28, 25, 23, 0.08)',
            fontSize: '1.1667rem',
          },
          success: {
            iconTheme: { primary: '#D4A853', secondary: '#FFFFFF' },
          },
          error: {
            iconTheme: { primary: '#D98A7E', secondary: '#FFFFFF' },
          },
          // Ordinary toasts fade. "Services updated" and "Card sent" are
          // confirmations of something the desk just did and is still looking
          // at; leaving them on screen means dismissing one after every save.
          //
          // The arrivals are the exception and set their own duration — nobody
          // is watching the screen when somebody walks in.
        }}
      >
        {(t) => (
          <ToastBar toast={t}>
            {({ icon, message }) => (
              <>
                {icon}
                {message}
                {t.duration === Infinity && (
                  <button
                    onClick={() => hotToast.dismiss(t.id)}
                    aria-label="Close"
                    style={{
                      marginLeft: '0.5rem', padding: '0 0.25rem', lineHeight: 1,
                      fontSize: '1.3333rem', color: '#A8A29E',
                      background: 'none', border: 'none', cursor: 'pointer',
                    }}>
                    ×
                  </button>
                )}
              </>
            )}
          </ToastBar>
        )}
      </Toaster>
    </>
  );
}

export default App;
